import * as React from 'react';
import { useEffect } from 'react';
import { useResultStore } from '@components/acca/results/result/resultStoreProvider';
import { AccVideoDetailResultVO } from '@domain/vo/acca/v1/test/result/apply/AccVideoResultVO';
import { VideoType, videoTypeStringMap } from '@domain/constant/acca/test/result/videoType';
import { videoQuestionTypeStringMap } from '@domain/constant/acca/test/result/videoQuestionType';
import classNames from 'classnames/bind';
import styles from './v4TabList.module.scss';

const cx = classNames.bind(styles);

// [D] 클래스 정리입니다.
// a.btnTab - 'hasSubTab': 서브탭 영역이 있는 경우 / 'on': 선택된 경우
// span.tabText - 'warning': 경고 아이콘 노출이 있는 경우
// a.btnSubTab - 'on': 선택된 경우

// [D] 기능 관련 정리입니다. (피그마에도 작성되어 있습니다.)
// a.btnTab
// - 서브탭 영역이 있는 경우: 서브탭 영역을 열고 닫는 역할('on' 클래스로 분기) / 서브탭 영역은 하나만 열립니다.
// - 서브탭 영역이 없는 경우: 우측에 영상 데이터 노출

interface V4TabListProps {
  videoDetailTab: AccVideoDetailResultVO | null;
  setVideoDetailTab: (videoDetailTab: AccVideoDetailResultVO | null) => void;
  openedSubTab: AccVideoDetailResultVO | null;
  setOpenedSubTab: (videoDetailTab: AccVideoDetailResultVO | null) => void;
}

const V4TabList = ({ videoDetailTab, setVideoDetailTab, openedSubTab, setOpenedSubTab }: V4TabListProps) => {
  const { accaResultStore } = useResultStore();
  const { accVideoDetailMap } = accaResultStore;
  const accVideoDetailResultList = accVideoDetailMap?.get('VIDEO_BASIC_EXPERIENCE');

  useEffect(() => {
    initTab();
  }, [accVideoDetailResultList]);

  const initTab = () => {
    if (!accVideoDetailResultList) return;
    const accVideoDetailResult = accVideoDetailResultList[0];
    setVideoDetailTab(accVideoDetailResult);
    setOpenedSubTab(accVideoDetailResult);
  };

  const getTabListEl = (videoType: VideoType) => {
    const accVideoDetailResultList = accVideoDetailMap?.get(videoType);
    if (!accVideoDetailResultList || accVideoDetailResultList.length === 0) return null;

    // 경험 기반 질문, 가치관 질문은 항상 꼬리 질문으로 존재
    const hasSubTab =
      accVideoDetailResultList.length > 1 ||
      videoType === 'VIDEO_BASIC_EXPERIENCE' ||
      videoType === 'VIDEO_BASIC_VALUES';

    const accVideoDetailResult = accVideoDetailResultList[0];
    const { voiceErrorYn, videoErrorYn } = accVideoDetailResult;
    const title = `${videoTypeStringMap[videoType]} 면접`;
    const isWarning = !hasSubTab && (voiceErrorYn || videoErrorYn);

    const getIsOnTab = () => {
      if (hasSubTab) return openedSubTab === accVideoDetailResult;
      else return videoDetailTab === accVideoDetailResult;
    };

    const handleClick = () => {
      if (hasSubTab) {
        if (openedSubTab === accVideoDetailResult) setOpenedSubTab(null);
        else setOpenedSubTab(accVideoDetailResult);
      } else setVideoDetailTab(accVideoDetailResult);
    };

    const subTabListEl = accVideoDetailResultList?.map((videoDetail, index) => {
      const { type, questionNum, voiceErrorYn, questionType, videoErrorYn } = videoDetail;
      const isOnTab = videoDetailTab === videoDetail;
      const isWarning = voiceErrorYn || videoErrorYn;
      const handleClick = () => setVideoDetailTab(videoDetail);
      const getTitle = () => {
        if (questionType === 'MAIN') return `${videoTypeStringMap[type]} 질문 ${questionNum}`;
        else return `관련 질문 ${questionNum}-${videoQuestionTypeStringMap[questionType]}`;
      };

      return (
        <a key={index} className={cx('btnSubTab', { on: isOnTab })} onClick={handleClick} role="button">
          <span className={cx('tabText', { warning: isWarning })}>{getTitle()}</span>
        </a>
      );
    });

    return (
      <div className={cx('tabWrap')}>
        <a className={cx('btnTab', { hasSubTab: hasSubTab }, { on: getIsOnTab() })} onClick={handleClick} role="button">
          <span className={cx('tabText', { warning: isWarning })}>{title}</span>
        </a>

        {/* [D] 서브탭 영역 */}
        {hasSubTab && <div className={cx('subTabArea')}>{subTabListEl}</div>}
      </div>
    );
  };

  if (!accVideoDetailMap) return null;

  return (
    <div className={cx('tabListArea')}>
      {getTabListEl('VIDEO_BASIC_EXPERIENCE')}
      {getTabListEl('VIDEO_BASIC_SITUATION')}
      {getTabListEl('VIDEO_BASIC_VALUES')}
      {getTabListEl('VIDEO_BASIC_FREE_CHOICE')}
    </div>
  );
};

export default V4TabList;
